import React, { useState, useEffect, useMemo } from "react";
import ExportButtons from "./ExportButtons";

const Customtable = ({
  table_title,
  columns,
  data,
  theadClass,
  divClassName,
  isBordered,
  isPagination = true,
  isGlobalFilter,
  buttonName,
  isAddButton,
  isExportButton,
  isCustomPageSize,
  handleUserClick,
  currentPage,
  setCurrentPage,
  loading,
  handleCheckboxChange,
  selectedIds,
  exportTitle,
}) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageSize, setPageSize] = useState(10);

  // ================= PER-COLUMN FILTERS =================
  const [columnFilters, setColumnFilters] = useState({});
  const [isColumnFilterEnabled, setIsColumnFilterEnabled] = useState(false);

  const handleColumnFilterChange = (key, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    internalSetCurrentPage(0);
  };

  const toggleColumnFilter = () => {
    setIsColumnFilterEnabled((prev) => !prev);
    setColumnFilters({});
    internalSetCurrentPage(0);
  };

  // ================= COLUMN MENU =================
  const [showColumnMenu, setShowColumnMenu] =
    useState(false);

  // ================= COLUMN VISIBILITY =================
  const [visibleColumns, setVisibleColumns] =
    useState([]);

  useEffect(() => {
    setVisibleColumns(
      columns
        .filter(
          (col) =>
            ![
              "Job Ref",
              "doc_count",
            ].includes(col.header) &&
            !col.accessorKey?.endsWith("_id") &&
            !col.accessorKey?.endsWith("_id_fk")
        )
        .map((col) => col.accessorKey)
    );
  }, [columns]);
  // CLOSE MENU ON OUTSIDE CLICK
  useEffect(() => {
    const closeMenu = () =>
      setShowColumnMenu(false);

    window.addEventListener("click", closeMenu);

    return () =>
      window.removeEventListener(
        "click",
        closeMenu
      );
  }, []);

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      // prevent hiding all columns
      if (
        prev.includes(key) &&
        prev.length === 1
      ) {
        return prev;
      }

      return prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key];
    });
  };

  const visibleCols = columns.filter((col) =>
    visibleColumns.includes(col.accessorKey)
  );

  // ================= CURRENT PAGE =================
  const internalCurrentPage = currentPage ?? 0;
  const internalSetCurrentPage =
    setCurrentPage ?? (() => { });

  // ================= FILTER (GLOBAL + PER-COLUMN) =================
  const filteredData = data.filter((row) => {
    const matchesGlobal =
      globalFilter.trim() === ""
        ? true
        : visibleCols.some((col) => {
          const value = row[col.accessorKey];

          return value
            ?.toString()
            .toLowerCase()
            .includes(globalFilter.toLowerCase());
        });

    if (!matchesGlobal) return false;

    if (!isColumnFilterEnabled) return true;

    return visibleCols.every((col) => {
      const filterValue = columnFilters[col.accessorKey];

      if (!filterValue || filterValue.trim() === "") return true;

      const value = row[col.accessorKey];

      return value
        ?.toString()
        .toLowerCase()
        .includes(filterValue.toLowerCase());
    });
  });

  const pageCount = Math.ceil(
    filteredData.length / pageSize
  );

  useEffect(() => {
    if (
      internalCurrentPage >= pageCount &&
      pageCount > 0
    ) {
      internalSetCurrentPage(0);
    }
  }, [
    filteredData,
    pageCount,
    internalCurrentPage,
    internalSetCurrentPage,
  ]);

  // ================= SORTING =================
  const [sortConfig, setSortConfig] =
    useState({
      key: null,
      direction: "asc",
    });

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? {
          key,
          direction:
            prev.direction === "asc"
              ? "desc"
              : "asc",
        }
        : {
          key,
          direction: "asc",
        }
    );
  };

  const sortedData = useMemo(() => {
    let sortable = [...filteredData];

    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);

        // NUMERIC SORT
        if (
          !isNaN(aNum) &&
          !isNaN(bNum)
        ) {
          return sortConfig.direction ===
            "asc"
            ? aNum - bNum
            : bNum - aNum;
        }

        // STRING SORT
        return sortConfig.direction ===
          "asc"
          ? aVal
            ?.toString()
            .localeCompare(bVal)
          : bVal
            ?.toString()
            .localeCompare(aVal);
      });
    }

    return sortable;
  }, [filteredData, sortConfig]);

  // ================= PAGINATION =================
  const currentPageData = sortedData.slice(
    internalCurrentPage * pageSize,
    (internalCurrentPage + 1) * pageSize
  );

  const visiblePageCount = 4;

  const [pageWindowStart, setPageWindowStart] =
    useState(0);

  const updatePage = (page) => {
    internalSetCurrentPage(page);

    if (
      page >=
      pageWindowStart +
      visiblePageCount -
      1
    ) {
      setPageWindowStart(
        page - visiblePageCount + 2
      );
    } else if (
      page <= pageWindowStart
    ) {
      setPageWindowStart(
        Math.max(0, page - 1)
      );
    }
  };

  const pageWindowEnd = Math.min(
    pageWindowStart + visiblePageCount,
    pageCount
  );

  // ================= FOOTER / EXPORT TOTALS =================
  // A column opts into totals by setting `isTotal: true` in its column def.
  // Totals are computed over the full filtered+sorted dataset (not just the
  // current page) so the footer shown on screen always matches the totals
  // row written into the export file.
  const totalableCols = useMemo(
    () => visibleCols.filter((col) => col.isTotal),
    [visibleCols]
  );

  const hasTotals = totalableCols.length > 0;

  // The "Total" label goes in the column immediately to the left of an
  // isTotal column (rather than always in the first column), so the label
  // sits right next to the number it explains. If an isTotal column has no
  // column before it, or the column before it is itself an isTotal column,
  // it's left without its own label.
  const totalLabelIndices = useMemo(() => {
    const indices = new Set();

    visibleCols.forEach((col, idx) => {
      if (col.isTotal && idx > 0 && !visibleCols[idx - 1].isTotal) {
        indices.add(idx - 1);
      }
    });

    return indices;
  }, [visibleCols]);

  const columnTotals = useMemo(() => {
    const totals = {};

    totalableCols.forEach((col) => {
      const rawTotal = sortedData.reduce((sum, row) => {
        const value = parseFloat(row[col.accessorKey]);
        return sum + (isNaN(value) ? 0 : value);
      }, 0);

      // Round to 3 decimal places, trimming floating-point noise
      // (e.g. 27524.091000000008 -> 27524.091).
      totals[col.accessorKey] = Math.round(rawTotal * 1000) / 1000;
    });

    return totals;
  }, [sortedData, totalableCols]);

  // Format a raw numeric total as a rupee string, e.g. 1234.5 -> "₹1,234.50".
  const formatCurrencyTotal = (numericValue) =>
    `₹${numericValue.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // ================= EXPORT DATA (visible columns, filtered + sorted, human-readable labels) =================
  const exportData = useMemo(() => {
    const jobStatusMap = {
      1: "Yet to Arrive",
      2: "BE Filed",
      3: "Arrived",
      4: "Check Duty Payment",
      5: "Examination",
      6: "OOC",
      7: "Ready to Despatch",
      8: "Lot Completed",
      9: "Billing",
    };

    const rows = sortedData.map((row, index) => {
      const exportRow = {};

      visibleCols.forEach((col) => {
        let value = row[col.accessorKey];

        switch (col.label_condition) {
          case "s_no":
            value = index + 1;
            break;

          case "status":
            value = value === "0" ? "Active" : "In-Active";
            break;

          case "job_status":
            value = jobStatusMap[value] || "Unknown";
            break;

          case "checkbox":
            value = value === 1 ? "Yes" : "No";
            break;

          case "checkbox_list":
            value = row[col.accessorKey] ? "Yes" : "No";
            break;

          case "html":
            // Strip HTML tags for a clean export value
            value = value?.toString().replace(/<[^>]*>/g, "");
            break;

          case "currency": {
            // Prefix with the rupee symbol for money columns (freight,
            // cost_per_ton, etc). Leaves blank/non-numeric values alone.
            const numericValue = parseFloat(value);
            value = isNaN(numericValue) ? value ?? "" : formatCurrencyTotal(numericValue);
            break;
          }

          default:
            break;
        }

        exportRow[col.header] = value ?? "";
      });

      return exportRow;
    });

    // Append a totals row at the end of the export, mirroring the on-screen footer.
    if (visibleCols.some((col) => col.isTotal)) {
      const totalsRow = {};

      visibleCols.forEach((col, idx) => {
        if (col.isTotal) {
          const rawTotal = sortedData.reduce((sum, row) => {
            const value = parseFloat(row[col.accessorKey]);
            return sum + (isNaN(value) ? 0 : value);
          }, 0);

          // Round to 3 decimal places, trimming floating-point noise
          // (e.g. 27524.091000000008 -> 27524.091).
          const roundedTotal = Math.round(rawTotal * 1000) / 1000;

          totalsRow[col.header] =
            col.label_condition === "currency"
              ? formatCurrencyTotal(roundedTotal)
              : roundedTotal;
        } else {
          totalsRow[col.header] = totalLabelIndices.has(idx) ? "Total" : "";
        }
      });

      rows.push(totalsRow);
    }

    return rows;
  }, [sortedData, visibleCols, totalLabelIndices]);

  return (
    <>
      {/* ================= HEADER ================= */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          justifyContent:
            "space-between",
          alignItems: "center",
        }}
      >
        <div className="d-flex gap-2 flex-wrap align-items-center">
          {/* PAGE SIZE */}
          {isCustomPageSize && (
            <select
              className="form-select w-auto"
              value={pageSize}
              onChange={(e) => {
                setPageSize(
                  Number(e.target.value)
                );
                internalSetCurrentPage(0);
              }}
            >
              {[10, 25, 50, 100].map(
                (s) => (
                  <option
                    key={s}
                    value={s}
                  >
                    {s}
                  </option>
                )
              )}
            </select>
          )}

          {/* EXPORT BUTTON */}
          {isExportButton && (
            <ExportButtons
              data={exportData}
              fileName={table_title}
              title={exportTitle}
              boldLastNRows={hasTotals ? 1 : 0}
            />
          )}

          {/* ================= COLUMN VISIBILITY ================= */}
          <div
            className="position-relative"
            style={{ zIndex: 1000 }}
          >
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              onClick={(e) => {
                e.stopPropagation();

                setShowColumnMenu(
                  !showColumnMenu
                );
              }}
            >
              <i className="ri-layout-column-line me-1"></i>
              Columns
            </button>

            {showColumnMenu && (
              <div
                className="bg-white border rounded shadow position-absolute mt-2 p-2"
                style={{
                  minWidth: "220px",
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
                onClick={(e) =>
                  e.stopPropagation()
                }
              >
                {columns.filter(
                  (col) =>
                    !["Job Ref", "doc_count"].includes(col.header) &&
                    !col.accessorKey?.endsWith("_id") &&
                    !col.accessorKey?.endsWith("_id_fk")
                ).map((col) => {
                  const isVisible =
                    visibleColumns.includes(
                      col.accessorKey
                    );

                  return (
                    <button
                      key={col.accessorKey}
                      type="button"
                      className={`dropdown-item d-flex justify-content-between align-items-center rounded ${isVisible
                        ? "active"
                        : ""
                        }`}
                      onClick={() =>
                        toggleColumn(
                          col.accessorKey
                        )
                      }
                    >
                      <span>
                        {col.header}
                      </span>

                      {isVisible && (
                        <i className="ri-check-line"></i>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* COLUMN FILTER TOGGLE */}
          <button
            className={`btn btn-sm ${isColumnFilterEnabled ? "btn-primary" : "btn-secondary"}`}
            type="button"
            onClick={toggleColumnFilter}
          >
            <i className="ri-filter-3-line me-1"></i>
            {isColumnFilterEnabled ? "Filters On" : "Filters Off"}
          </button>

          {/* ADD BUTTON */}
          {isAddButton && (
            <button
              className="btn btn-primary btn-sm"
              onClick={handleUserClick}
            >
              + {buttonName}
            </button>
          )}
        </div>

        {/* SEARCH */}
        {isGlobalFilter && (
          <input
            type="search"
            placeholder="Search..."
            className="form-control w-100"
            style={{
              maxWidth: "250px",
            }}
            value={globalFilter}
            onChange={(e) =>
              setGlobalFilter(
                e.target.value
              )
            }
          />
        )}
      </div>

      {/* ================= DESKTOP TABLE ================= */}
      <div className="d-none d-md-block mt-3">
        <div className="table-responsive">
          <table
            className={`table ${isBordered
              ? "table-bordered"
              : ""
              } ${divClassName ?? ""}`}
            style={{
              width: "100%",
              tableLayout: "auto",
            }}
          >
            <thead className={theadClass ?? ""}>
              <tr>
                {visibleCols.map((col) => (
                  <th
                    key={col.accessorKey}
                    onClick={() =>
                      handleSort(
                        col.accessorKey
                      )
                    }
                    style={{
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      width: "auto",
                      padding: "10px 12px",
                      verticalAlign: "middle",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        width: "max-content",
                      }}
                    >
                      <span>{col.header}</span>

                      {sortConfig.key ===
                        col.accessorKey && (
                          <span>
                            {sortConfig.direction ===
                              "asc"
                              ? "▲"
                              : "▼"}
                          </span>
                        )}
                    </div>
                  </th>
                ))}
              </tr>

              {isColumnFilterEnabled && (
                <tr>
                  {visibleCols.map((col) => {
                    const skipFilter =
                      col.label_condition === "checkbox_list" ||
                      col.label_condition === "checkbox" ||
                      col.label_condition === "s_no";

                    return (
                      <th
                        key={`filter-${col.accessorKey}`}
                        style={{
                          padding: "4px 12px 10px",
                          fontWeight: "normal",
                        }}
                      >
                        {!skipFilter && (
                          <input
                            type="search"
                            className="form-control form-control-sm"
                            placeholder={`Search ${col.header}`}
                            value={columnFilters[col.accessorKey] || ""}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              handleColumnFilterChange(
                                col.accessorKey,
                                e.target.value
                              )
                            }
                          />
                        )}
                      </th>
                    );
                  })}
                </tr>
              )}
            </thead>

            <tbody>
              {currentPageData.length ? (
                currentPageData.map(
                  (
                    row,
                    rowIndex
                  ) => (
                    <tr key={rowIndex}>
                      {visibleCols.map(
                        (col) => {
                          const cellValue =
                            row[
                            col
                              .accessorKey
                            ];

                          const tdStyle = {
                            whiteSpace:
                              "nowrap",
                            width: "auto",
                            padding:
                              "10px 12px",
                            verticalAlign:
                              "middle",
                          };

                          switch (
                          col.label_condition
                          ) {
                            case "s_no":
                              return (
                                <td
                                  key={
                                    col.accessorKey
                                  }
                                  style={
                                    tdStyle
                                  }
                                >
                                  {internalCurrentPage *
                                    pageSize +
                                    rowIndex +
                                    1}
                                </td>
                              );

                            case "status":
                              return (
                                <td
                                  key={
                                    col.accessorKey
                                  }
                                  style={
                                    tdStyle
                                  }
                                >
                                  <span
                                    className={`status-badge ${cellValue ===
                                      "0"
                                      ? "status-active"
                                      : "status-inactive"
                                      }`}
                                  >
                                    {cellValue ===
                                      "0"
                                      ? "Active"
                                      : "In-Active"}
                                  </span>
                                </td>
                              );

                            case "job_status":
                              const statusMap =
                              {
                                1: {
                                  class:
                                    "s-yet",
                                  text: "Yet to Arrive",
                                },
                                2: {
                                  class:
                                    "s-filed",
                                  text: "BE Filed",
                                },
                                3: {
                                  class:
                                    "s-arrived",
                                  text: "Arrived",
                                },
                                4: {
                                  class:
                                    "s-duty",
                                  text: "Check Duty Payment",
                                },
                                5: {
                                  class:
                                    "s-exam",
                                  text: "Examination",
                                },
                                6: {
                                  class:
                                    "s-ooc",
                                  text: "OOC",
                                },
                                7: {
                                  class:
                                    "s-desp",
                                  text: "Ready to Despatch",
                                },
                                8: {
                                  class:
                                    "s-done",
                                  text: "Lot Completed",
                                },
                              };

                              const status =
                                statusMap[
                                cellValue
                                ] || {
                                  class:
                                    "s-unknown",
                                  text: "Unknown",
                                };

                              return (
                                <td
                                  key={
                                    col.accessorKey
                                  }
                                  style={
                                    tdStyle
                                  }
                                >
                                  <span
                                    className={`status ${status.class}`}
                                  >
                                    {
                                      status.text
                                    }
                                  </span>
                                </td>
                              );

                            case "checkbox":
                              return (
                                <td
                                  key={
                                    col.accessorKey
                                  }
                                  style={
                                    tdStyle
                                  }
                                >
                                  <span
                                    className={`status-badge ${cellValue ===
                                      1
                                      ? "status-active"
                                      : "status-inactive"
                                      }`}
                                  >
                                    {cellValue ===
                                      1
                                      ? "Yes"
                                      : "No"}
                                  </span>
                                </td>
                              );

                            case "checkbox_list":
                              return (
                                <td
                                  key={
                                    col.accessorKey
                                  }
                                  style={
                                    tdStyle
                                  }
                                >
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={selectedIds?.includes(
                                      row.ticket_id_fk
                                    )}
                                    onChange={(
                                      e
                                    ) =>
                                      handleCheckboxChange(
                                        e,
                                        row.ticket_id_fk
                                      )
                                    }
                                  />
                                </td>
                              );

                            case "html":
                              return (
                                <td
                                  key={
                                    col.accessorKey
                                  }
                                  style={
                                    tdStyle
                                  }
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      cellValue,
                                  }}
                                />
                              );

                            case "currency": {
                              const numericValue = parseFloat(cellValue);
                              const displayValue = isNaN(numericValue)
                                ? cellValue ?? ""
                                : formatCurrencyTotal(numericValue);

                              return (
                                <td
                                  key={col.accessorKey}
                                  style={tdStyle}
                                >
                                  {displayValue}
                                </td>
                              );
                            }

                            default:
                              return col.cell ? (
                                <td
                                  key={
                                    col.accessorKey
                                  }
                                  style={
                                    tdStyle
                                  }
                                >
                                  {col.cell({
                                    row,
                                  })}
                                </td>
                              ) : (
                                <td
                                  key={
                                    col.accessorKey
                                  }
                                  style={
                                    tdStyle
                                  }
                                >
                                  {cellValue}
                                </td>
                              );
                          }
                        }
                      )}
                    </tr>
                  )
                )
              ) : (
                <tr>
                  <td
                    colSpan={
                      visibleCols.length
                    }
                    className="text-center py-4"
                  >
                    {loading
                      ? "Loading..."
                      : "No result found"}
                  </td>
                </tr>
              )}
            </tbody>

            {/* ================= FOOTER TOTALS ================= */}
            {hasTotals && currentPageData.length > 0 && (
              <tfoot>
                <tr>
                  {visibleCols.map((col, idx) => (
                    <td
                      key={`footer-${col.accessorKey}`}
                      style={{
                        whiteSpace: "nowrap",
                        padding: "10px 12px",
                        fontWeight: "bold",
                        borderTop: "2px solid #dee2e6",
                      }}
                    >
                      {col.isTotal
                        ? col.label_condition === "currency"
                          ? formatCurrencyTotal(columnTotals[col.accessorKey])
                          : columnTotals[col.accessorKey]
                        : totalLabelIndices.has(idx)
                          ? "Total"
                          : ""}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* ================= PAGINATION ================= */}
        {isPagination &&
          pageCount > 0 && (
            <div
              className="d-flex justify-content-between align-items-center flex-wrap mt-3"
              style={{
                gap: "10px",
              }}
            >
              {/* LEFT INFO */}
              <div
                style={{
                  fontSize: "14px",
                }}
              >
                Showing{" "}
                <strong>
                  {internalCurrentPage *
                    pageSize +
                    1}
                </strong>{" "}
                to{" "}
                <strong>
                  {Math.min(
                    (internalCurrentPage +
                      1) *
                    pageSize,
                    filteredData.length
                  )}
                </strong>{" "}
                of{" "}
                <strong>
                  {
                    filteredData.length
                  }
                </strong>{" "}
                entries
              </div>

              {/* PAGINATION */}
              <div className="d-flex align-items-center gap-1 flex-wrap">
                {/* PREVIOUS */}
                <button
                  className="btn btn-sm btn-light border"
                  disabled={
                    internalCurrentPage ===
                    0
                  }
                  onClick={() =>
                    updatePage(
                      internalCurrentPage -
                      1
                    )
                  }
                >
                  Prev
                </button>

                {/* FIRST */}
                {pageWindowStart >
                  0 && (
                    <>
                      <button
                        className="btn btn-sm btn-light border"
                        onClick={() =>
                          updatePage(0)
                        }
                      >
                        1
                      </button>

                      {pageWindowStart >
                        1 && (
                          <span className="px-1">
                            ...
                          </span>
                        )}
                    </>
                  )}

                {/* PAGE NUMBERS */}
                {Array.from(
                  {
                    length:
                      pageWindowEnd -
                      pageWindowStart,
                  },
                  (_, i) =>
                    pageWindowStart +
                    i
                ).map((page) => (
                  <button
                    key={page}
                    className={`btn btn-sm ${internalCurrentPage ===
                      page
                      ? "btn-primary"
                      : "btn-light border"
                      }`}
                    onClick={() =>
                      updatePage(page)
                    }
                  >
                    {page + 1}
                  </button>
                ))}

                {/* LAST */}
                {pageWindowEnd <
                  pageCount && (
                    <>
                      {pageWindowEnd <
                        pageCount -
                        1 && (
                          <span className="px-1">
                            ...
                          </span>
                        )}

                      <button
                        className="btn btn-sm btn-light border"
                        onClick={() =>
                          updatePage(
                            pageCount - 1
                          )
                        }
                      >
                        {pageCount}
                      </button>
                    </>
                  )}

                {/* NEXT */}
                <button
                  className="btn btn-sm btn-light border"
                  disabled={
                    internalCurrentPage ===
                    pageCount - 1
                  }
                  onClick={() =>
                    updatePage(
                      internalCurrentPage +
                      1
                    )
                  }
                >
                  Next
                </button>
              </div>
            </div>
          )}
      </div>
    </>
  );
};

export default Customtable;